'use client';

import { type IDefaultFormBuilderProps } from '@/components/Form/Fields/types';
import Label from '@/components/Label/Label';
import InfoIconModal from '@/components/infoIcon/InfoIconModal';
import { RichText } from '@/components/richText/RichText';
import { SwdCircularProgress } from '@/components/swdCircularProgress/SwdCircularProgress';
import { TextField } from '@/components/textField';
import { hasContent } from '@/helper/RichText';
import { useBankService } from '@/hooks/useBankService';
import type { FormIbanStoryblok, FormTextStoryblok } from '@/types/component-types-sb';
import { type FieldProps, useFormikContext } from 'formik';
import { type FC, useEffect, useRef, useState } from 'react';

import { type FormValues } from '../../types';

interface IInputField extends Omit<FormIbanStoryblok, 'id'>, FieldProps, IDefaultFormBuilderProps {}

const InputField: FC<IInputField> = ({
  field,
  id,
  label,
  placeholder,
  infoText,
  errorMessage,
  isInvalid,
  required,
  disabled,
  type,
  readOnly,
  bicField,
  bankField,
}) => {
  const { values, setValues } = useFormikContext<FormValues>();
  const { getBank, isLoading, validateGermanIbanOffline } = useBankService();
  useEffect(() => {
    if (!field.value) {
      return;
    }

    getBank(field.value)
      .then((bankData) => {
        if (bankData) {
          const { bic, bank } = bankData;
          setValues(
            {
              ...values,
              [bicField]: bic ?? values[bicField] ?? '',
              [bankField]: bank ?? values[bankField] ?? '',
            },
            true
          );
        } else if (validateGermanIbanOffline(field.value)) {
          // Use offline validation as fallback if getBank fails but IBAN is valid
          setValues(
            {
              ...values,
              [bicField]: values[bicField] ?? '',
              [bankField]: values[bankField] ?? '',
            },
            true
          );
        }
      })
      .catch((error) => {
        console.error('Error fetching bank data:', error);
        // Try offline validation as fallback
        if (validateGermanIbanOffline(field.value)) {
          setValues(
            {
              ...values,
              [bicField]: values[bicField] ?? '',
              [bankField]: values[bankField] ?? '',
            },
            true
          );
        }
      });
  }, [field.value]);

  return (
    <TextField
      {...field}
      id={id}
      type={type}
      disabled={disabled}
      placeholder={placeholder}
      label={label && <Label text={label} />}
      helperText={isInvalid && errorMessage}
      error={isInvalid}
      fullWidth
      required={required}
      slotProps={{
        htmlInput: {
          'data-testid': 'form-builder-iban-input',
          tabIndex: readOnly ? -1 : 0,
        },
        input: {
          endAdornment: (
            <>
              {isLoading && <SwdCircularProgress size={16} />}
              {hasContent(infoText) && (
                <InfoIconModal>
                  <RichText text={infoText} />
                </InfoIconModal>
              )}
            </>
          ),
          readOnly,
        },
      }}
    />
  );
};

export default InputField;
